import React, { useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const MultiExamComparisonChart = ({ exams }) => {
  const [selectedSubjectType, setSelectedSubjectType] = useState('TYT');
  if (!exams || exams.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        Karşılaştırmak için deneme seçin
      </div>
    );
  }

  // Sort exams by date
  const sortedExams = [...exams].sort((a, b) => 
    new Date(a.examDate) - new Date(b.examDate)
  );

  // Prepare data for line chart (Total Net Progress) - TYT and AYT separate lines
  const lineChartData = {
    labels: sortedExams.map(exam => 
      new Date(exam.examDate).toLocaleDateString('tr-TR', { 
        day: '2-digit', 
        month: 'short' 
      })
    ),
    datasets: [
      // TYT Line (Blue)
      {
        label: 'TYT Net',
        data: sortedExams.map(exam => 
          exam.examType === 'TYT' ? exam.totalNet : null
        ),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointRadius: 6,
        pointHoverRadius: 8,
        pointBackgroundColor: 'rgb(59, 130, 246)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        spanGaps: true,
      },
      // AYT Line (Purple)
      {
        label: 'AYT Net',
        data: sortedExams.map(exam => 
          exam.examType === 'AYT' ? exam.totalNet : null
        ),
        borderColor: 'rgb(168, 85, 247)',
        backgroundColor: 'rgba(168, 85, 247, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointRadius: 6,
        pointHoverRadius: 8,
        pointBackgroundColor: 'rgb(168, 85, 247)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        spanGaps: true,
      }
    ]
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          font: { size: 12, weight: '600' },
          padding: 15,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        },
        callbacks: {
          label: function(context) {
            return `Net: ${context.parsed.y.toFixed(2)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            size: 12
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 11
          }
        }
      }
    }
  };

  // Subject definitions for TYT and AYT
  const tytSubjects = [
    { key: 'turkish', name: 'Türkçe', color: 'rgba(239, 68, 68, 0.8)' },
    { key: 'math', name: 'Matematik', color: 'rgba(59, 130, 246, 0.8)' },
    { key: 'science', name: 'Fen', color: 'rgba(168, 85, 247, 0.8)' },
    { key: 'social', name: 'Sosyal', color: 'rgba(249, 115, 22, 0.8)' }
  ];

  const aytSubjects = [
    { key: 'mathAyt', name: 'Matematik', color: 'rgba(59, 130, 246, 0.8)' },
    { key: 'physics', name: 'Fizik', color: 'rgba(168, 85, 247, 0.8)' },
    { key: 'chemistry', name: 'Kimya', color: 'rgba(34, 197, 94, 0.8)' },
    { key: 'biology', name: 'Biyoloji', color: 'rgba(249, 115, 22, 0.8)' }
  ];

  // Use selected subject type
  const subjects = selectedSubjectType === 'TYT' ? tytSubjects : aytSubjects;

  // Filter exams by selected type for bar chart
  const filteredExams = sortedExams.filter(e => e.examType === selectedSubjectType);
  const lastExam = filteredExams[filteredExams.length - 1];
  const firstExam = filteredExams[0];

  // If no exams of selected type, show empty state
  if (!lastExam) {
    return (
      <div className="text-center py-8 text-gray-500">
        {selectedSubjectType} denemesi bulunamadı
      </div>
    );
  }

  const barChartData = {
    labels: subjects.map(s => s.name),
    datasets: filteredExams.length > 1 ? [
      {
        label: 'İlk Deneme',
        data: subjects.map(s => firstExam?.nets?.[s.key] || 0),
        backgroundColor: 'rgba(156, 163, 175, 0.6)',
        borderColor: 'rgba(156, 163, 175, 1)',
        borderWidth: 2,
        borderRadius: 6,
      },
      {
        label: 'Son Deneme',
        data: subjects.map(s => lastExam?.nets?.[s.key] || 0),
        backgroundColor: subjects.map(s => s.color),
        borderColor: subjects.map(s => s.color.replace('0.8', '1')),
        borderWidth: 2,
        borderRadius: 6,
      }
    ] : [
      {
        label: 'Net',
        data: subjects.map(s => lastExam?.nets?.[s.key] || 0),
        backgroundColor: subjects.map(s => s.color),
        borderColor: subjects.map(s => s.color.replace('0.8', '1')),
        borderWidth: 2,
        borderRadius: 6,
      }
    ]
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: sortedExams.length > 1,
        position: 'top',
        labels: {
          font: {
            size: 12,
            weight: '600'
          },
          padding: 15,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        },
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y.toFixed(2)} net`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            size: 12
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 12,
            weight: '500'
          }
        }
      }
    }
  };

  return (
    <div className="space-y-8">
      {/* Toplam Net İlerleme - Line Chart */}
      {sortedExams.length > 1 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-4">
            📈 Toplam Net İlerleme Grafiği
          </h4>
          <div className="h-64">
            <Line data={lineChartData} options={lineChartOptions} />
          </div>
        </div>
      )}

      {/* Ders Bazlı Karşılaştırma - Bar Chart */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold text-gray-700">
            📊 {filteredExams.length > 1 ? 'İlk ve Son Deneme Karşılaştırması' : 'Ders Bazlı Net Dağılımı'}
          </h4>
          <select
            value={selectedSubjectType}
            onChange={(e) => setSelectedSubjectType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="TYT">TYT Dersleri</option>
            <option value="AYT">AYT Dersleri</option>
          </select>
        </div>
        <div className="h-64">
          <Bar data={barChartData} options={barChartOptions} />
        </div>
      </div>

      {/* Özet İstatistikler */}
      {filteredExams.length > 1 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg">
            <p className="text-xs text-blue-600 font-semibold mb-1">İlk Deneme ({selectedSubjectType})</p>
            <p className="text-2xl font-bold text-blue-900">
              {firstExam?.totalNet.toFixed(2)}
            </p>
            <p className="text-xs text-blue-600 mt-1">
              {new Date(firstExam?.examDate).toLocaleDateString('tr-TR')}
            </p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg">
            <p className="text-xs text-green-600 font-semibold mb-1">Son Deneme ({selectedSubjectType})</p>
            <p className="text-2xl font-bold text-green-900">
              {lastExam?.totalNet.toFixed(2)}
            </p>
            <p className="text-xs text-green-600 mt-1">
              {new Date(lastExam?.examDate).toLocaleDateString('tr-TR')}
            </p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg">
            <p className="text-xs text-purple-600 font-semibold mb-1">Değişim</p>
            <p className={`text-2xl font-bold ${
              (lastExam?.totalNet - firstExam?.totalNet) >= 0 
                ? 'text-green-900' 
                : 'text-red-900'
            }`}>
              {(lastExam?.totalNet - firstExam?.totalNet) >= 0 ? '+' : ''}
              {(lastExam?.totalNet - firstExam?.totalNet).toFixed(2)}
            </p>
            <p className="text-xs text-purple-600 mt-1">Net fark</p>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg">
            <p className="text-xs text-orange-600 font-semibold mb-1">Ortalama</p>
            <p className="text-2xl font-bold text-orange-900">
              {(filteredExams.reduce((sum, exam) => sum + exam.totalNet, 0) / filteredExams.length).toFixed(2)}
            </p>
            <p className="text-xs text-orange-600 mt-1">{filteredExams.length} deneme</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiExamComparisonChart;
